import { delimit } from '../helpers/listsHelpers';

const revisionLabel = (prefix: string, revision?: number) => delimit([prefix, revision && `Revision (${revision})`], ' - ');
const createRevisionLabel = (prefix: string) => revisionLabel.bind(null, prefix);
const adminLabels = {
    id: 'Id',
    key: 'Key',
    copy: 'Copy',
    download: 'Download',
    delete: 'Delete',
    clear: 'Clear',
    add: 'Add',
    create: 'Create',
    save: 'Save',
    cancel: 'Cancel',
    role: 'Role',
    avatar: 'Avatar',
    icon: 'Landing Page Icon',
    name: 'Name',
    type: 'Type',
    edit: 'Edit',
    model: 'Model',
    seeMore: 'See more',
    seeLess: 'See less',
    confirm: 'Confirm',
    search: 'Search',
    routeKey: 'Route key',
    description: 'Description',
    saveChanges: 'Save changes',
    createNew: 'Create new',
    createVersionHeadline: 'Create an Assistant Version',
    autoSaveHeadline: 'Enable autosave, your changes will be saved every few seconds once you stop typing. or you can individually pick desired version in current: Assistant > Share > Beta section',
    requiredHelperText: 'This field is required',

    searchNotFound: 'Cannot find any results for your search criteria',
    searchPlaceholder: 'Enter search criteria',

    publishBeta: 'Publish Beta',
    autoPublishBeta: 'Automatically publish this version for testing',
    publishBetaDescription: 'Publish Beta version to the testing audience, granting them early access to new features to explore, identify issues, and provide feedback.',
    createDraftDescription: 'Create a new draft version to work on features, improvements, or bug fixes without affecting the live version.',
    publishLive: 'Publish Live ',
    publishLiveDescription: 'Release this version to all users, replacing the previous version with the new one. This makes the latest features available to everyone, ensuring a seamless experience while introducing enhancements to improve performance and usability.',
    publishLiveRestrictedHeadline: 'Publishing is restricted',
    publishLiveRestrictedMessage: 'Only admins are allowed to publish live versions. Please contact your administrator for assistance.',
    publishLiveWithBeta: createRevisionLabel('Publish current beta'),
    publishLiveWithLatest: createRevisionLabel('Publish latest revision'),

    invalidStateForPublishing: "Publishing disabled due to invalid state.",
    workflowToolRequired: "Workflow assistant must have a workflow tool assigned",
    agentModelRequired: "Assistant requires an agentic model",
    modelRequired: "Assistant requires agentic model or chat model.",

    published: 'Published',
    beta: 'Beta',
    live: 'Live',
    draft: 'Draft',

    noPermissionTitle: 'This action is disabled due to missing permissions',

    //
    sectionsHeadline: 'Sections',
    sectionsDescription: 'You can select between sections of interest.',
    assistantsHeadline: 'Assistants',
    assistantsDescription: 'Create, manage and publish assistants, their versions and configurations.',
    assistantsNoAccess: 'You do not have access to assistants, please contact your administrator.',
    assistantsVersionNoAccess: 'You do not have access to assistant versions, please contact your administrator.',
    assistantsEvaluationNoAccess: 'You do not have access to assistant evaluation, please contact your administrator.',

    //Models
    modelsHeadline: 'Models',
    modelsDescription: 'Create and manage models and their configurations.',
    modelsNoAccess: 'You do not have access to models, please contact your administrator.',

    // Tools
    toolsHeadline: 'Tools',
    toolsDescription: 'Manage audiences and their permissions seamlessly.',
    toolsNoAccess: 'You do not have access to tools, please contact your administrator.',
    toolConfigurationHeadline: 'Configurations',
    toolAudiencesHeadline: 'Audiences',
    toolAudienceRestrictedMessage: "You don't have permission to grant access to user groups. You can only assign individual users. For help with group access, please contact an administrator.",



    //
    toolTab: 'Tool',
    settingsTab: 'Settings',
    promptTab: 'Prompt',
    knowledgeTab: 'Knowledge',
    changelogTab: 'Changelog',
    publishTab: 'Publish/Revise',

    // Elements
    historyPlaceholder: 'Assistants',
    addTextPlaceholder: 'Add text',
    tableSearchPlaceholder: 'Search',
    tableSearchNoResult: 'No result found on your search criteria',
    listingNameInputTile: 'Assistant name',
    uniqueListingNameValidationText: 'Assistant name should be unique',
    listingNameConvention: 'Assistant name should follow this naming convention: ”{Segment} {Name} Assistant”. For example, "DCS Alsmart Assistant”',
    listingDescriptionInputTile: 'Description',
    removeIcon: 'Remove icon',
    getAvatarUsageWarning: (usageCount: number) =>
        usageCount > 0
            ? `This avatar is already in use by ${usageCount} other assistant${usageCount === 1 ? '' : 's'}.`
            : `This avatar is not used by any other assistant.`,
    admin: 'Admin',

    // Models
    addToolTitle: 'Tools',
    addMemberTypeTitle: 'Type',
    addMemberRoleTitle: 'Role',
    addMember: 'Add member',

    addToolHeadline: 'Add Tool',

    editToolHeadline: 'Edit Tool',
    editToolOptionsHeadline: 'Tool Options',
    editToolFunctionsHeadline: 'Tool Functions',
    editToolOptionsUnavailable: 'No options available',

    editAudienceHeadline: 'Edit audience',

    addMemberSearchTitle: 'Search',
    addMemberHeadline: 'Add audience',
    noVersionsFound: 'You have no versions created yet',
    noToolsAvailable: 'No tools has been added yet',
    createVersionActionTitle: 'Source',
    createVersionVersionTitle: 'Version',
    createVersionSelectionPlaceholder: 'New version',
    createAssistantHeadline: 'Create an Assistant',
    createAssistantToggle: 'Manually define assistant key',
    addMemberNoResult: 'No result found on your search criteria',
    editConfiguration: 'Edit configuration',
    addMemberSearchNote: 'Search to find desired result',
    createVersionVersionPlaceholder: 'Version number (x.x.x-tag-x)',
    createVersionIncorrectFormat: 'Invalid version format, please use the following format: (major.minor.patch)',
    keyIncorrectFormat: 'Invalid input. Value can contain only numbers, lower case characters, dashes and underscores and must not start with a number.',

    createEvaluationHeadline: 'Create an evaluation collection',
    createEvaluationNameTitle: 'Name',
    createEvaluationDescriptionTitle: 'Description',
    evaluationManualEntryCountTitle: 'Manual count',
    evaluationSessionEntryCountTitle: 'Session count',
    noEvaluationsFound: 'You have not created evaluation collection yet',
    couldNotCopyEvaluation: 'Could not copy evaluation at this time. Please try again later or contact support.',

    runEvaluation: 'Start evaluation',
    runCreateEvaluation: 'Create an evaluation',
    runEvaluationTitle: 'Create an evaluation run',
    runEvaluationOfTitle: 'Evaluation Run',
    runEvaluationNameTitle: 'Name',
    runEvaluationAssistantVersionTitle: 'Assistant version',
    runEvaluationRevisionTitle: 'Revision',
    runEvaluationDescriptionTitle: 'Description',
    runEvaluationStrictnessTitle: 'Evaluation Strictness',
    runEvaluationCriteriaTitle: 'Evaluation Criteria',
    runEvaluationCollectionTitle: 'Evaluation collection',
    seeEvaluationReport: 'View Detailed Report',
    noEvaluationRunsFound: 'You have not created evaluation run yet',
    runQueueWarning: 'The process may take longer than usual due to high demand and queue times. Thank you for your patience.',
    noEntriesFound: 'You have not created evaluation entries yet',
    evaluationEntryIntro: 'Select an evaluation entry to edit or create a new one',
    evaluationEntryQuestionInputLabel: 'Question',
    evaluationEntryFactInputLabel: 'Fact',
    evaluationEntryReasonInputLabel: 'Reason',
    evaluationEntryDesiredInputLabel: 'Is desired?',

    listingEnableInAdvancedAssistant: 'Enable this model appearance in Advanced assistant',
    listingMaxCharacters: 'Maximum character amount',
    assistantMaxCharactersDescription: 'Override the max character amount of the model. The max character amount of the model will be used if this is set to empty or "0"',
    listingOrder: 'Order in the list',

    modelConfigSelectedProvider: 'Selected model provider..',
    modelConfigProviderLabel: 'Provider',
    modelConfigDeploymentLabel: 'Deployment',
    modelConfigWorkspaceLabel: 'Workspace',
    modelConfigEndpointLabel: 'Endpoint',
    modelConfigStreamingLabel: 'Streaming',
    modelConfigMaxOutputTokensLabel: 'Max Output Tokens',
    modelConfigLocationLabel: 'Location',


    deleteTitle: 'Delete item',
    deleteHeadline: 'Delete',
    deleteDescription: 'Are you sure you want to delete following items: ',

    semVerCta: 'Need help with naming?',
    semVerHeadline: `We're using Semantic Versioning (SemVer)`,
    semVerDescription: `
    This versioning method helps us to version software using <u>MAJOR.MINOR.PATCH</u> plus optional pre-release and build metadata tags <u>(e.g., 1.2.3-alpha.1)</u>.
    <br/>
    <br/>

    <b>Versioning rules:</b><br/>
    MAJOR: Increase for breaking changes (e.g., 1.0.0 → 2.0.0).<br/>
    MINOR: Increase for new features that don't break compatibility (e.g., 1.2.0 → 1.3.0).<br/>
    PATCH: Increase for bug fixes or small improvements (e.g., 1.2.3 → 1.2.4).<br/>
    `,
    noVersionsAvailable: 'No versions available, please publish a version first.',




    // Routes
    assistantsRouteCta: 'Create an assistant',
    modelsRouteCta: 'Create a model',
    changelogRouteReleaseNotesTitle: 'Release notes',
    changelogRouteHighlightsTitle: 'Highlights',

    evaluationRouteHeadline: 'Evaluation',
    evaluationRouteDisclaimer: 'The evaluation feature is in development, please use the chat UI to Evaluate',
    evaluationRouteCta: 'open in a new tab',
    evaluationCollectionsSectionHeadline: 'Collections',
    evaluationRunSectionHeadline: 'Run',
    manualTab: 'MANUAL EVALUATION',
    sessionTab: 'SESSION EVALUATION',



    statisticsRouteHeadline: 'Statistics',
    statisticsRouteDisclaimer: 'The statistics feature is in development.',
    knowledgeRouteToolsCta: 'Add tool',
    settingsRouteListingTitle: 'Listing',
    settingsRouteConfigurationsTitle: 'Configurations',
    settingsRouteAudiencesTitle: 'Audiences',
    settingsRouteConversationStartersTitle: 'Conversation starters',
    settingsRouteVersionsTitle: 'Versions',
    shareRouteLiveTitle: 'Live',
    shareRouteBetaTitle: 'Beta',
    shareRouteMembersTitle: 'Members',
    toolsRouteAssistantHeadline: 'Assistant tools',
    toolsRouteAssistantContent: 'Assistant tools config form',
    promptRouteMacrosTitle: 'Macros',
    promptRouteMacrosBlankInfo: 'Macros are available onces instructions are added or tools, documents or are included in the knowledge tab',
    knowledgeRouteDocumentsTitle: createRevisionLabel('Documents'),
    knowledgeRouteModelTitle: createRevisionLabel('Model'),
    knowledgeRouteToolsTitle: createRevisionLabel('Tools'),
    promptRouteSystemTitle: createRevisionLabel('System prompt'),
    promptRouteInstructionsTitle: createRevisionLabel('Instructions'),
    instructionNameMissingName: '*',
    instructionsNameLabel: 'Name',
    instructionsDescriptionLabel: 'Description',
    instructionsTemplateLabel: 'Template',
    noInstructionsYet: 'No instructions yet',
    createInstruction: 'Create instruction',
    deleteInstruction: (name: string) => `Delete instruction: ${name}`,
    publishRevisionTitle: "Publish Current Revision",
    publishBetaTitle: createRevisionLabel('Publish Beta'),
    publishLiveTitle: createRevisionLabel('Publish Live'),
    publishLiveWarningDescription: 'This version will be published and made available to all selected audiences specified in Audience tab. Do you want to continue?',
    revisionTitle: createRevisionLabel(''),
    createRevisionTitle: 'Create New Revision',


    // versionData?.build?.latest
    createNewRevisionCta: (currentRevision?: number) => revisionLabel('Create New', currentRevision ? currentRevision + 1 : undefined),
    temperature: 'Temperature',

    agentModel: 'Agentic Model',
    agentModelDescription: 'Choose the model which the assistant uses for its ReAct agent.<br/>The selected model must support tool/function calling for the assistant to work correctly once tools are included.',
    chatModel: 'Conversation Model',
    chatModelDescription: 'Optionally, choose a different model to use when the assistant is not consuming any tools (ad-hoc documents, etc..).<br/>This option can be useful to reduce cost.<br/><br/>For example, you can use Gemini 1.5 Flash as a conversation model and then switch to using GPT 4o when tools are needed.<br/><br/>If chat model is not selected, the Agentic model will be used.',

    // errors
    promptSavingError: 'Unable autosave prompt',
    createAudienceErrorHeadline: 'Error creating audience',
    createAudienceErrorMessage: 'Please try again, and contact us if the problem persists',
    loadAudienceErrorHeadline: 'Error loading data',
    loadAudienceErrorMessage: 'Please try reloading the page and contact us if the problem persists',
    generalErrorMessage: 'Please try again, and contact us if the problem persists',

    createToolErrorHeadline: 'Error creating tool',
    createToolErrorMessage: 'Please try again, and contact us if the problem persists',

    deleteErrorHeadline: 'Unable to delete selected items',
    deleteErrorMessage: 'Please try again, and contact us if the problem persists',

    createAssistantErrorHeadline: 'Unable to create an assistant',
    createAssistantErrorMessage: 'Please try again, and contact us if the problem persists',

    createVersionErrorHeadline: 'Unable to create a version',
    createVersionErrorMessage: 'Please try again, and contact us if the problem persists',

    editConfigurationErrorHeadline: 'Unable to edit configuration',
    editConfigurationErrorMessage: 'Please try again, and contact us if the problem persists',

    updateAudienceErrorHeadline: 'Unable to update audience(s)',
    updateAudienceErrorMessage: 'Please try again, and contact us if the problem persists',

    globalSaveErrorHeadline: 'Unable to save changes',
    globalSaveErrorMessage: 'Please try again, and contact us if the problem persists',

    createModelHeadline: 'Create a model',
    createModelToggle: 'Manually define model key',
};

export default adminLabels;
